import React, { useCallback, useEffect, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import { DealRedirect, MISSING_FIELD_IDS_KEY, REQUIRED_FOR_STAGE_KEY } from "constants/deal";
import { Routes } from "constants/routes";
import { useModals, useNavigate } from "hooks";
import { useDealIdSelector, useDealRedirectSelector, useDealStatusSelector, useViewActions } from "store";
import { IStatus } from "store/types";

// Status list of deals that should show dashboard page at the beginning
const dashboardStatusDeals: Array<IStatus["name"]> = ["Invested", "Closed"];

// Search param keys used when navigating from "Review missing fields" so we preserve them on redirect

// This is hook listens to the deal state, content and redirect
export const useDealRedirect = (): React.Dispatch<React.SetStateAction<boolean>> => {
  const dealId = useDealIdSelector();
  const status = useDealStatusSelector();
  const dealRedirect = useDealRedirectSelector();

  const [isListen, setIsListen] = useState<boolean>(false);

  const { setDealRedirectAction } = useViewActions();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { errorModal } = useModals();

  // When URL has "Review missing fields" params, preserve them across redirects so the characteristics bar opens
  const preservedSearch = useMemo(() => {
    const params = new URLSearchParams(search);
    if (!params.has(REQUIRED_FOR_STAGE_KEY) || !params.has(MISSING_FIELD_IDS_KEY)) return undefined;
    return Object.fromEntries(params.entries());
  }, [search]);

  // Find anything in workspace and redirect
  const redirectToWorkspace = useCallback(
    (dealId: number) => {
      return navigate({
        route: "dealWorkspace",
        params: { deal_id: dealId },
        ...(preservedSearch && { search: preservedSearch })
      });
    },
    [errorModal, preservedSearch]
  );

  // Specific redirects
  const handleForceRedirect = useCallback(
    (dealRedirect: DealRedirect) => {
      if (!dealId) return;
      if (dealRedirect === DealRedirect.Dashboard) {
        navigate({
          route: "dealDashboard",
          params: { deal_id: dealId },
          ...(preservedSearch && { search: preservedSearch })
        });
      }

      if (dealRedirect === DealRedirect.Workspace) {
        navigate({
          route: "dealWorkspace",
          params: { deal_id: dealId },
          ...(preservedSearch && { search: preservedSearch })
        });
      }
    },
    [dealRedirect, dealId, preservedSearch]
  );

  const makeInitialRedirect = useCallback(() => {
    if (!dealId) return;
    // Redirect to dashboard show deals with a specific status
    if (dashboardStatusDeals.some((item) => item === status?.name)) {
      return navigate({
        route: "dealDashboard",
        params: { deal_id: dealId },
        ...(preservedSearch && { search: preservedSearch })
      });
    }

    // Redirect any item from databox that can be found for
    redirectToWorkspace(dealId);
  }, [dealId, status, redirectToWorkspace, preservedSearch]);

  useEffect(() => {
    if (!isListen) return;
    if (!dealRedirect) return;
    handleForceRedirect(dealRedirect);
    setDealRedirectAction(null);
  }, [dealRedirect, isListen]);

  useEffect(() => {
    if (!isListen) return; // Don't run until deal let it do
    if (!status) return; // Impossible to decide where to redirect without status
    if (dealRedirect) return; // Skip operation if redirect was specified
    if (
      !matchPath(pathname, {
        path: Routes.deal,
        exact: true,
        strict: true
      })
    )
      return; // Skip operation if route is not default
    makeInitialRedirect();
  }, [dealId, isListen]);

  return setIsListen;
};
